'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { motion } from 'framer-motion'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { AnimatedTabIndicatorProvider, useActiveTabIndicator } from './animatedTabIndicator'

type SegmentedControlSize = 'sm' | 'default'

const SegmentedControlSizeContext = React.createContext<SegmentedControlSize>('sm')

type SegmentedControlListProps = React.ComponentProps<typeof TabsPrimitive.List> & {
  size?: SegmentedControlSize
}

function SegmentedControlList({ className, size = 'sm', ...props }: SegmentedControlListProps) {
  return (
    <AnimatedTabIndicatorProvider>
      <SegmentedControlSizeContext.Provider value={size}>
        <TabsPrimitive.List
          data-slot="segmented-control-list"
          className={cn(
            'bg-accent inline-flex w-fit items-center gap-0.5 rounded-lg p-0.5',
            className
          )}
          {...props}
        />
      </SegmentedControlSizeContext.Provider>
    </AnimatedTabIndicatorProvider>
  )
}

function SegmentedControlItem({
  className,
  children,
  value,
  ref: externalRef,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  const size = React.useContext(SegmentedControlSizeContext)
  const { layoutId, isActive, ref } = useActiveTabIndicator(externalRef)

  return (
    <TabsPrimitive.Trigger
      ref={ref}
      value={value}
      data-slot="segmented-control-item"
      className={cn(
        'text-foreground text-2xs relative inline-flex cursor-pointer items-center justify-center rounded-md px-2 py-1',
        size === 'default' && 'h-7 py-0',
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4 [&_svg[width='20'][height='20']:not([class*='size-'])]:size-5",
        'transition-colors duration-100 hover:bg-muted/50 active:bg-muted/40',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent',
        'data-[state=active]:hover:bg-transparent data-[state=active]:active:bg-transparent',
        className
      )}
      {...props}
    >
      {isActive && layoutId ? (
        <motion.span
          layoutId={layoutId}
          layoutDependency={value}
          className="border-border bg-secondary absolute inset-0 rounded-md border-[0.5px] shadow-sm"
          transition={{ type: 'spring', bounce: 0.1, duration: 0.25 }}
        />
      ) : null}
      <span className="relative z-10 inline-flex items-center justify-center gap-1">
        {children}
      </span>
    </TabsPrimitive.Trigger>
  )
}

export { SegmentedControlItem, SegmentedControlList }
